import { useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tooltip, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, TextField, Button, MenuItem, Select,
  FormControl, InputLabel,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { getAdminLogs } from '../../api/partnerApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESETS = [
  { label: '30m', minutes: 30 },
  { label: '1h',  minutes: 60 },
  { label: '6h',  minutes: 360 },
  { label: '24h', minutes: 1440 },
];

const RESOURCES = ['', 'documents', 'records', 'search', 'schemas', 'folders', 'clients', 'orgs', 'users'];
const METHODS   = ['', 'GET', 'POST', 'PUT', 'DELETE'];

function toIso(date) {
  return date.toISOString().slice(0, 16); // datetime-local format
}

function defaultWindow(minutes = 60) {
  const end   = new Date();
  const start = new Date(end.getTime() - minutes * 60 * 1000);
  return { startTime: toIso(start), endTime: toIso(end) };
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }) {
  if (!status) return null;
  const color = status >= 500 ? 'error' : status >= 400 ? 'warning' : 'success';
  return <Chip label={status} size="small" color={color} sx={{ fontSize: '0.7rem', height: 20 }} />;
}

// ---------------------------------------------------------------------------
// Method chip
// ---------------------------------------------------------------------------

const METHOD_COLORS = {
  GET:    { bg: '#e3f2fd', color: '#1565c0' },
  POST:   { bg: '#e8f5e9', color: '#2e7d32' },
  PUT:    { bg: '#fff8e1', color: '#f57f17' },
  DELETE: { bg: '#fce4ec', color: '#c62828' },
};

function MethodChip({ method }) {
  const cfg = METHOD_COLORS[method] || { bg: '#f5f5f5', color: '#616161' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
      bgcolor: cfg.bg, color: cfg.color,
    }}>
      {method || '—'}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Resource chip
// ---------------------------------------------------------------------------

function ResourceChip({ resource }) {
  if (!resource || resource === 'null') return <span style={{ color: '#9e9e9e' }}>—</span>;
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      fontSize: '0.7rem', fontWeight: 600,
      bgcolor: '#f3e5f5', color: '#6a1b9a',
    }}>
      {resource}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ApiLogsPage() {
  const { user } = useAuth();
  const [env, setEnv]             = useState('test');
  const [window, setWindow]       = useState(defaultWindow(60));
  const [resource, setResource]   = useState('');
  const [method, setMethod]       = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [queryKey, setQueryKey]   = useState(null); // null = not yet fetched

  const tenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  const buildParams = useCallback(() => ({
    tenantId,
    startTime: new Date(window.startTime).toISOString(),
    endTime:   new Date(window.endTime).toISOString(),
    ...(resource   && { resource }),
    ...(method     && { method }),
    ...(errorsOnly && { errorsOnly: 'true' }),
    limit: 200,
  }), [tenantId, window, resource, method, errorsOnly]);

  const { data, isFetching, error } = useQuery(
    ['api-logs', queryKey],
    // Migrated from developerApi.getApiLogs (now-deleted /developer/logs/api)
    // to partnerApi.getAdminLogs (/v1/admin/logs). Same params + response
    // shape — auth shifts from Cognito JWT to an st_* minted via the
    // playground-token endpoint, scoped to the env selector's tenant.
    () => getAdminLogs(env, buildParams()),
    {
      enabled: queryKey !== null,
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  const handleFetch = () => {
    // Force a new query key so re-clicking always re-fetches
    setQueryKey(Date.now());
  };

  const applyPreset = (minutes) => {
    setWindow(defaultWindow(minutes));
  };

  const entries = data?.entries || [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} mb={3}>API Logs</Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection="column" gap={2}>

          {/* Env + time presets */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup exclusive size="small" value={env}
              onChange={(_, v) => v && setEnv(v)}>
              <ToggleButton value="test" sx={{ fontSize: '0.8rem', px: 2 }}>Test</ToggleButton>
              <ToggleButton value="live" sx={{ fontSize: '0.8rem', px: 2 }}>Live</ToggleButton>
            </ToggleButtonGroup>

            <Box display="flex" gap={0.5}>
              {PRESETS.map(p => (
                <Button key={p.label} size="small" variant="outlined"
                  onClick={() => applyPreset(p.minutes)}
                  sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }}>
                  {p.label}
                </Button>
              ))}
            </Box>

            <TextField size="small" type="datetime-local" label="From"
              value={window.startTime}
              onChange={e => setWindow(w => ({ ...w, startTime: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }} />
            <TextField size="small" type="datetime-local" label="To"
              value={window.endTime}
              onChange={e => setWindow(w => ({ ...w, endTime: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }} />
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Resource</InputLabel>
              <Select value={resource} label="Resource" onChange={e => setResource(e.target.value)}>
                {RESOURCES.map(r => (
                  <MenuItem key={r} value={r}>{r || 'All resources'}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Method</InputLabel>
              <Select value={method} label="Method" onChange={e => setMethod(e.target.value)}>
                {METHODS.map(m => (
                  <MenuItem key={m} value={m}>{m || 'All methods'}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButton size="small" value="errors" selected={errorsOnly}
              onChange={() => setErrorsOnly(v => !v)}
              sx={{ fontSize: '0.75rem', px: 1.5, color: errorsOnly ? 'error.main' : undefined }}>
              Errors only
            </ToggleButton>

            <Button variant="contained" size="small" onClick={handleFetch}
              disabled={isFetching || !tenantId}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <Refresh />}>
              {isFetching ? 'Querying…' : 'Fetch logs'}
            </Button>

            {data && !isFetching && (
              <Typography variant="caption" color="text.secondary">
                {entries.length}{data.truncated ? ' (truncated at 200)' : ''} entries
                {' · '}{data.queryDurationMs}ms
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Results */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.data?.message || error.message || 'Query failed'}
        </Alert>
      )}

      {isFetching && entries.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isFetching && data && entries.length === 0 && (
        <Typography color="text.secondary">No log entries found in this time window.</Typography>
      )}

      {entries.length > 0 && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, width: 160 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Method</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }}>Resource</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 90 }}>Latency</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 140 }}>
                    <Tooltip title="The credential that made the call. Prefixes: sk_ = root API key, ssk_ = scoped API key, st_ = short-lived scoped token.">
                      <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>Key ID</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={i} sx={{
                    bgcolor: e.status >= 500 ? 'error.50'
                           : e.status >= 400 ? 'warning.50'
                           : 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {e.timestamp ? new Date(e.timestamp).toLocaleTimeString([], {
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                        fractionalSecondDigits: 3,
                      }) : '—'}
                    </TableCell>
                    <TableCell><MethodChip method={e.method} /></TableCell>
                    <TableCell><ResourceChip resource={e.resource} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={e.path || ''}>
                        <span>{e.path || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><StatusChip status={e.status} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      color: e.durationMs > 5000 ? 'error.main'
                           : e.durationMs > 2000 ? 'warning.main' : 'text.secondary' }}>
                      {e.durationMs != null ? `${e.durationMs}ms` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: 'text.secondary' }}>
                      <Tooltip title={e.keyId || ''}>
                        <span>{e.keyId || '—'}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {data.truncated && (
            <Typography variant="caption" color="warning.main" sx={{ p: 1.5, display: 'block' }}>
              Results truncated at 200 — narrow the time window or add filters to see all logs.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
